import { useState, useEffect } from "react";
import { PageHero, PillButton } from "../components/ui-kit";
import { Stagger, StaggerItem } from "../components/motion";
import { getTransformations } from "../lib/api";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import LazyImage from "../components/LazyImage";

const filters = ["All", "Fat Loss", "Muscle Gain", "Strength", "Body Recomposition"];

export default function Transformations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getTransformations()
      .then((d) => setItems(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const filtered = filter === "All" ? items : items.filter((t) => t.category === filter);

  return (
    <div data-testid="transformations-page">
      <PageHero label="Client Transformations" title={<>Proof over <span className="text-accent">promises.</span></>}
        subtitle="Every transformation is a real client who trusted the process. Yours could be next." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-3 mb-12">
            {filters.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase().replace(/ /g,'-')}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${filter === f ? "bg-accent text-white" : "glass hover:text-accent"}`}>
                {f}
              </button>
            ))}
          </div>

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="transformations-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} data-testid="transformation-skeleton" className="rounded-2xl overflow-hidden glass">
                  <div className="skeleton aspect-[4/5]" />
                  <div className="p-6 space-y-2">
                    <div className="skeleton h-4 w-1/2 rounded" />
                    <div className="skeleton h-3 w-1/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="py-16 text-center text-muted-foreground" data-testid="transformation-empty">
              No transformations in this category yet.
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {filtered.map((t) => (
                  <StaggerItem key={t.id}>
                    <motion.div layout onClick={() => setSelected(t)} data-testid={`transformation-${t.id}`}
                      className="rounded-2xl overflow-hidden glass hover-lift group cursor-pointer">
                      {t.composite ? (
                        <div className="relative overflow-hidden aspect-[4/5]">
                          <LazyImage src={t.after} alt={t.name} className="absolute inset-0 transition-transform duration-500 group-hover:scale-105" />
                          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-accent text-white px-2.5 py-1 rounded-full font-bold z-10">Before / After</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2">
                          <div className="relative overflow-hidden aspect-[3/4]">
                            <LazyImage src={t.before} alt="Before" className="absolute inset-0 transition-transform duration-500 group-hover:scale-110" />
                            <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 text-white px-2 py-1 rounded-full z-10">Before</span>
                          </div>
                          <div className="relative overflow-hidden aspect-[3/4]">
                            <LazyImage src={t.after} alt="After" className="absolute inset-0 transition-transform duration-500 group-hover:scale-110" />
                            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-accent text-white px-2 py-1 rounded-full z-10">After</span>
                          </div>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-heading font-bold">{t.name}</h3>
                          <span className="text-accent font-heading font-black">{t.result}</span>
                        </div>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t.category} • {t.duration}</p>
                      </div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </AnimatePresence>
            </Stagger>
          )}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl glass border-border p-0 overflow-hidden" data-testid="transformation-modal">
          {selected && (
            <div>
              {selected.composite ? (
                <div className="relative aspect-[4/5] w-full">
                  <img src={selected.after} alt={selected.name} className="w-full h-full object-contain bg-black" />
                </div>
              ) : (
                <div className="grid grid-cols-2">
                  <div className="relative aspect-[3/4]"><img src={selected.before} alt="Before" className="w-full h-full object-cover" /><span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 text-white px-2 py-1 rounded-full">Before</span></div>
                  <div className="relative aspect-[3/4]"><img src={selected.after} alt="After" className="w-full h-full object-cover" /><span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-accent text-white px-2 py-1 rounded-full">After</span></div>
                </div>
              )}
              <div className="p-8">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-black text-2xl">{selected.name}</h3>
                  <span className="text-accent font-heading font-black text-2xl">{selected.result}</span>
                </div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">{selected.category} • {selected.duration}</p>
                <p className="text-lg leading-relaxed mb-6">"{selected.quote}"</p>
                <PillButton to="/coaching" testid="modal-cta">Start Your Transformation <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="py-20 border-t border-border text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-8">Your before photo is waiting.</h2>
          <PillButton to="/coaching" testid="transformations-cta">Apply For Coaching <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
        </div>
      </section>
    </div>
  );
}
